import { memo, useEffect, useMemo, useState } from 'react';
import { cn } from '@/utils/cn';
import { TChannelOption } from '@/features/channels/_shared/types/channel.type';
import { getChannelIcon } from '@/utils/get-channel-icon';

interface IAvatarProps {
  channel?: TChannelOption;
  username?: string;
  imgSrc?: string | null | undefined;
  className?: string;
  isActive?: boolean;
  onClick?: () => void;
  stopPropagation?: boolean;
  isWithBorder?: boolean;
  classNamePlaceholder?: string;
  channelClassName?: string;
  isGroup?: boolean;
}

const Avatar = memo<IAvatarProps>(
  ({
    isActive = false,
    className,
    imgSrc,
    onClick,
    username,
    channel,
    stopPropagation = true,
    isWithBorder = false,
    classNamePlaceholder,
    channelClassName,
    isGroup = false,
  }) => {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
      setImgError(false);
    }, [imgSrc]);

    const channelIcon = useMemo(() => {
      return getChannelIcon(channel || 'whatsapp');
    }, [channel]);

    const imgPlaceholder = useMemo(() => {
      return (
        <div className="w-full bg-[#1F445A] text-white h-full uppercase rounded-full absolute top-0 left-0 flex items-center justify-center">
          <span className={cn('text-[16px] font-medium', classNamePlaceholder)}>
            {username ? username?.split(' ').join('+')[0] : '~'}
          </span>
        </div>
      );
    }, [username, classNamePlaceholder]);

    const imgGroupPlaceholder = useMemo(() => {
      return (
        <div className="w-full bg-[#1F445A]  h-full rounded-full absolute top-0 left-0 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className={cn('w-[20px] h-[20px] text-white font-medium', classNamePlaceholder)}
          >
            <path
              d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16 3.12793C16.8578 3.3503 17.6174 3.85119 18.1597 4.55199C18.702 5.25279 18.9962 6.11382 18.9962 6.99993C18.9962 7.88604 18.702 8.74707 18.1597 9.44787C17.6174 10.1487 16.8578 10.6496 16 10.8719"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 20.9999V18.9999C21.9993 18.1136 21.7044 17.2527 21.1614 16.5522C20.6184 15.8517 19.8581 15.3515 19 15.1299"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }, [classNamePlaceholder]);

    return (
      <div
        className={cn(
          'w-[40px] cursor-pointer h-[40px] shrink-0 rounded-full  relative ',
          className,
          isWithBorder && 'border border-white'
        )}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
          }
          onClick?.();
        }}
      >
        {!imgSrc || imgError ? (
          <>{isGroup ? imgGroupPlaceholder : imgPlaceholder}</>
        ) : (
          <>
            {isGroup ? (
              imgGroupPlaceholder
            ) : (
              <img
                onError={() => setImgError(true)}
                src={imgSrc}
                alt={`${username} avatar`}
                className={cn('w-full h-full object-cover rounded-full border border-white ')}
              />
            )}
          </>
        )}
        {isActive && <div className="absolute bottom-[4px] right-0 w-2 h-2 bg-green-500 rounded-full" />}
        {channel && (
          <img
            src={channelIcon}
            alt="channel"
            className={cn('absolute bottom-0 right-0 w-[16px] h-[16px] object-cover rounded-full', channelClassName)}
          />
        )}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;
